import React, { useEffect, useMemo, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { api, authHeaders } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, X, Loader2 } from "lucide-react";
import InterestsPicker from "@/components/InterestsPicker";

// Gentle, dismissable nudge shown after sign-up: completing interests makes
// the "dedicated offers" more precise. Not blocking.
export default function ProfileSuggestionBanner({ client, onDone }) {
  const cid = client?.client_account_id;
  const flag = cid ? `kn_profilesuggest_${cid}` : null;
  const [dismissed, setDismissed] = useState(() => (flag ? localStorage.getItem(flag) === "1" : false));
  const [open, setOpen] = useState(false);
  const [macros, setMacros] = useState([]);
  const [interests, setInterests] = useState(client?.interests || []);
  const [saving, setSaving] = useState(false);

  const pct = useMemo(() => {
    if (!client) return 0;
    const checks = [client.name, client.phone, client.city, client.birth_year, client.gender, client.interests && client.interests.length];
    const done = checks.filter(Boolean).length;
    return Math.round((done / checks.length) * 100);
  }, [client]);

  useEffect(() => { setInterests(client?.interests || []); }, [client]);

  useEffect(() => {
    if (open && macros.length === 0) {
      api.get("/categories").then((r) => setMacros(r.data.categories || r.data || [])).catch(() => {});
    }
  }, [open, macros.length]);

  // Required demographics already present (so we are not mid mandatory-profile flow)
  // and no interests chosen yet → show the suggestion.
  const shouldShow = useMemo(
    () => !!client && !!client.birth_year && !!client.gender && !(client.interests && client.interests.length) && !dismissed,
    [client, dismissed]
  );

  if (!shouldShow) return null;

  const dismiss = () => { if (flag) localStorage.setItem(flag, "1"); setDismissed(true); };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/client/auth/profile", { interests }, { headers: authHeaders("client") });
      toast.success("Perfetto! Ora le offerte saranno più su misura per te.");
      if (flag) localStorage.setItem(flag, "1");
      setOpen(false);
      setDismissed(true);
      if (onDone) await onDone();
    } catch (_) {
      toast.error("Non è stato possibile salvare. Riprova.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div data-testid="profile-suggestion-banner" className="kn-fade-up relative overflow-hidden rounded-2xl border border-[#E63946]/15 bg-gradient-to-br from-[#FFF1F2] to-white p-4 pr-10 flex items-center gap-3">
        <div className="h-11 w-11 rounded-xl kn-gradient grid place-items-center shrink-0 shadow-sm">
          <Sparkles className="h-5 w-5 text-white" />
        </div>
        <div className="min-w-0">
          <p className="font-extrabold text-slate-900 leading-tight">Offerte dedicate più precise</p>
          <p className="text-sm text-slate-600 mt-0.5">Completa il profilo con i tuoi gusti: ti mostreremo per primi gli sconti giusti per te.</p>
          <div className="mt-2 max-w-[260px]">
            <p className="text-xs font-bold text-slate-500">Profilo completo al {pct}%</p>
            <div className="mt-1 h-1.5 w-full rounded-full bg-slate-200 overflow-hidden">
              <div className="h-full kn-gradient rounded-full transition-all duration-500" style={{ width: `${pct}%` }} />
            </div>
          </div>
          <button data-testid="profile-suggestion-cta" onClick={() => setOpen(true)}
            className="mt-2 inline-flex items-center gap-1.5 text-sm font-bold text-[#E63946] hover:underline">
            Completa il profilo <span aria-hidden>→</span>
          </button>
        </div>
        <button data-testid="profile-suggestion-dismiss" onClick={dismiss} aria-label="Chiudi"
          className="absolute top-2.5 right-2.5 h-7 w-7 rounded-full hover:bg-black/5 grid place-items-center text-slate-400">
          <X className="h-4 w-4" />
        </button>
      </div>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="bottom" className="rounded-t-3xl max-h-[85dvh] overflow-y-auto" data-testid="profile-suggestion-sheet">
          <SheetHeader className="text-left">
            <SheetTitle className="text-xl font-extrabold">Cosa ti interessa?</SheetTitle>
          </SheetHeader>
          <p className="text-sm text-slate-600 -mt-1">Scegli le categorie che ami. Useremo i tuoi gusti per mostrarti offerte più precise e vicine a te.</p>
          <div className="mt-4" data-testid="profile-suggestion-interests">
            <InterestsPicker macros={macros} value={interests} onChange={setInterests} prefix="ps-interest" />
          </div>
          <div className="flex items-center gap-3 mt-6 pb-2">
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="ps-later" className="flex-1 h-12 rounded-xl font-bold">Più tardi</Button>
            <Button onClick={save} disabled={saving || interests.length === 0} data-testid="ps-save" className="flex-1 h-12 rounded-xl font-bold">
              {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : "Salva"}
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
